import filter from 'lodash/filter';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { FrequencyConfig, IntervalTypes } from 'common/types/frequencySelector/frequencySelector';
import { ScheduleSendValues } from './types/scheduleSend';

/**
 * Validates if the scheduled report is completely valid and ready for submission.
 * This method:
 * - Returns true only when ALL required fields are valid
 * - Validation includes interval types and timezones
 * - Used for determining if form can be submitted
 * - Does not modify any UI state
 *
 * @returns {boolean} true if all required fields are valid
 */
export const isValidScheduledReport = (scheduleSendValues: ScheduleSendValues): boolean => {
  const { frequencyConfig } = scheduleSendValues;
  const { hasEndDate } = frequencyConfig.datesInfo;
  const { type } = frequencyConfig.intervalInfo;
  const withEndDateArray = hasEndDate ? ['endDate'] : [];
  const requireDateFields = ['startDate', ...withEndDateArray];
  const requireTimeFields = ['time', 'timeZone'];
  const requireIntervalFields = ['count', 'type'];
  const commonFields = ['name', 'subject', 'recipients'];
  const emptyCommonFields = filter(commonFields, (field) => isEmpty(get(scheduleSendValues, field)));
  const validIntervals: IntervalTypes[] = ['day', 'week', 'month'];
  if (!isEmpty(emptyCommonFields)) {
    return false;
  }
  if (!validIntervals.includes(type as IntervalTypes)) {
    return false;
  }

  if (type === 'week') {
    requireIntervalFields.push('days');
  } else if (type === 'month') {
    requireIntervalFields.push('days');
    requireIntervalFields.push('week');
  }
  const emptyRequireIntervalFields = filterRequiredFields(
    frequencyConfig,
    requireIntervalFields,
    'intervalInfo',
  );
  const emptyRequireTimeFields = filterRequiredFields(frequencyConfig, requireTimeFields, 'timeInfo');
  const emptyRequireDateFields = filterRequiredFields(frequencyConfig, requireDateFields, 'datesInfo');
  return (
    isEmpty(emptyRequireTimeFields) && isEmpty(emptyRequireIntervalFields) && isEmpty(emptyRequireDateFields)
  );
};

const filterRequiredFields = (frequencyConfig: FrequencyConfig, section: string[], key: string) => {
  const frequencyConfigObject = get(frequencyConfig, key);
  return filter(section, (field) => {
    const value = get(frequencyConfigObject, field);
    if (field === 'count' && isIntervalInvalid(value)) {
      return true;
    }
    return isEmpty(value?.toString());
  });
};

interface InvalidFieldProps {
  scheduleSendValues: ScheduleSendValues;
  setRequiredFieldsStatus: React.Dispatch<
    React.SetStateAction<{
      isFrequencyTimeInvalid: boolean;
      isFrequencyStartDateInvalid: boolean;
      isFrequencyEndDateInvalid: boolean;
      isRecipientEmptyUponSaving: boolean;
      isIntervalInvalid: boolean;
    }>
  >;
}

/**
 * Validates required fields and sets specific error states for UI feedback.
 * This method:
 * - Sets individual error state flags for each field type
 * - Returns true if ANY field is invalid (opposite of isValidScheduledReport)
 * - Focuses on UI state management rather than overall validation
 * - Validation does not include interval types and timezones
 *
 * @returns {boolean} true if any required field is invalid
 */
export const withEmptyRequiredField = ({
  scheduleSendValues,
  setRequiredFieldsStatus,
}: InvalidFieldProps) => {
  const { timeInfo, datesInfo, intervalInfo } = scheduleSendValues.frequencyConfig;

  const isTimeInvalid = isNil(timeInfo.time);
  const isStartDateInvalid = isNil(datesInfo.startDate) || datesInfo.startDate === '';
  const isEndDateInvalid = datesInfo.hasEndDate && (isNil(datesInfo.endDate) || datesInfo.endDate === '');
  const isRecipientsInvalid = isEmpty(scheduleSendValues.recipients);
  const isScheduleNameInvalid = isEmpty(scheduleSendValues.name);
  const isEmailSubjectInvalid = isEmpty(scheduleSendValues.subject);

  setRequiredFieldsStatus({
    isFrequencyTimeInvalid: isTimeInvalid,
    isFrequencyStartDateInvalid: isStartDateInvalid,
    isFrequencyEndDateInvalid: isEndDateInvalid,
    isRecipientEmptyUponSaving: isRecipientsInvalid,
    isIntervalInvalid: isIntervalInvalid(intervalInfo.count),
  });

  return (
    isTimeInvalid ||
    isStartDateInvalid ||
    isEndDateInvalid ||
    isRecipientsInvalid ||
    isScheduleNameInvalid ||
    isEmailSubjectInvalid ||
    isIntervalInvalid(intervalInfo.count)
  );
};

const isIntervalInvalid = (value: number): boolean => {
  return isUndefined(value) || isNaN(value) || value < 1;
};
